// ========================================
// BrandingModal — Logo + Favicon upload in one modal.
//
// Replaces the old gear-menu "Change Logo" file picker. Two upload
// cards, each auto-uploads on file-select (no batch save). Favicon
// preview shows both 16x16 (actual tab size) and 64x64 so the user
// sees what browsers really render — not just the modal-sized image.
//
// Style mirrors Dashboard.js Company Settings modal for consistency.
// ========================================

import React, { useRef, useState } from 'react';
import api from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import { useSalesRoomLabel } from '../hooks/useSalesRoomLabel';

function getAssetUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const baseUrl = process.env.REACT_APP_API_URL || '';
  return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
}

export default function BrandingModal({ onClose }) {
  const { tenant, refetchTenant } = useTenant();
  const { label: roomLabel } = useSalesRoomLabel();
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [error, setError] = useState(null);

  // Bust browser image cache when previewing newly-uploaded files —
  // gallery URLs are content-addressed by timestamp in the filename, so
  // a new upload changes the URL anyway, but for SVG/ICO replacements
  // at the same path this guarantees the preview refreshes.
  const [cacheBust, setCacheBust] = useState(0);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError(null);
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      await api.post('/api/tenant/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refetchTenant();
      setCacheBust((n) => n + 1);
    } catch (err) {
      console.error('Logo upload failed:', err);
      setError('Logo upload failed. Check the file size (max 5 MB) and try again.');
    } finally {
      setUploadingLogo(false);
      // Reset input so the same filename can be re-selected
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError(null);
    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('favicon', file);
      await api.post('/api/tenant/favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refetchTenant();
      setCacheBust((n) => n + 1);
    } catch (err) {
      console.error('Favicon upload failed:', err);
      setError('Favicon upload failed. Check the file size (max 5 MB) and try again.');
    } finally {
      setUploadingFavicon(false);
      if (faviconInputRef.current) faviconInputRef.current.value = '';
    }
  };

  const logoPreview = getAssetUrl(tenant?.logo_url);
  const faviconPreview = getAssetUrl(tenant?.favicon_url);
  const previewSuffix = cacheBust > 0 ? `?v=${cacheBust}` : '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-semibold text-gray-900">Logo &amp; Favicon</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* LOGO */}
          <div className="border border-gray-200 rounded-xl p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="text-base font-medium text-gray-900">Company Logo</h4>
                <p className="text-xs text-gray-500 mt-1">
                  Shown in the dashboard header and in your {roomLabel} templates.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-5">
              <div className="flex-shrink-0 w-32 h-20 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                {logoPreview ? (
                  <img
                    src={`${logoPreview}${previewSuffix}`}
                    alt="Current logo"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-gray-400">No logo</span>
                )}
              </div>

              <div className="flex-1">
                <input
                  type="file"
                  ref={logoInputRef}
                  className="hidden"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={handleLogoUpload}
                />
                <button
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {uploadingLogo ? 'Uploading…' : logoPreview ? 'Replace logo' : 'Upload logo'}
                </button>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                  Transparent background recommended. Roughly 80px tall.
                  PNG, SVG or WebP. Max 5 MB.
                </p>
              </div>
            </div>
          </div>

          {/* FAVICON */}
          <div className="border border-gray-200 rounded-xl p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="text-base font-medium text-gray-900">Favicon</h4>
                <p className="text-xs text-gray-500 mt-1">
                  Browser-tab icon on your public {roomLabel} pages.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-5">
              <div className="flex-shrink-0 flex items-end gap-3">
                {/* 64x64 preview */}
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                    {faviconPreview ? (
                      <img
                        src={`${faviconPreview}${previewSuffix}`}
                        alt="Favicon 64x64"
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <span className="text-[10px] text-gray-400">None</span>
                    )}
                  </div>
                  <span className="text-[10px] text-gray-500 mt-1">64×64</span>
                </div>
                {/* 16x16 preview (actual tab size) */}
                <div className="flex flex-col items-center">
                  <div className="w-4 h-4 bg-gray-50 border border-gray-200 rounded-sm flex items-center justify-center overflow-hidden">
                    {faviconPreview ? (
                      <img
                        src={`${faviconPreview}${previewSuffix}`}
                        alt="Favicon 16x16"
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : null}
                  </div>
                  <span className="text-[10px] text-gray-500 mt-1">16×16</span>
                </div>
              </div>

              <div className="flex-1">
                <input
                  type="file"
                  ref={faviconInputRef}
                  className="hidden"
                  accept="image/png,image/svg+xml,image/x-icon,image/vnd.microsoft.icon,.ico"
                  onChange={handleFaviconUpload}
                />
                <button
                  onClick={() => faviconInputRef.current?.click()}
                  disabled={uploadingFavicon}
                  className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {uploadingFavicon ? 'Uploading…' : faviconPreview ? 'Replace favicon' : 'Upload favicon'}
                </button>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                  Square image, 64×64 minimum (512×512 recommended).
                  PNG, SVG or ICO. Max 5 MB. Browsers cache favicons
                  aggressively — hard-reload to see changes.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 font-medium"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
